import React from 'react';
import {Platform, SafeAreaView, ScrollView, View} from 'react-native';
import Button from '../../components/elements/Button';
import Divider from '../../components/elements/Divider';
import Text from '../../components/elements/Text';
import Input from '../../components/elements/Input';
import BreadCrumbs from '../../components/elements/BreadCrumbs';
import makeStyles from './styles.web';
import {useTheme, useThemedStyles} from '../../theme';
import {tID} from '../../utils/TestIdHelper';
import CommonStyles from '../../utils/CommonStyles';
import {BUTTON_TYPES} from '../../utils/Constants';
import {
  ClosedEyeIcon,
  OpenEyeIcon,
  LogoGroup,
  LeftArrowIcon,
} from '../../utils/SvgIcons';

const UI = ({
  isValidForm,
  password,
  showPassword,
  repeatPassword,
  showRepeatPassword,
  hasPasswordError,
  hasRepeatPasswordError,
  setPassword,
  setRepeatPassword,
  onPressSave,
  onPressSkip,
  onPressShowPassword,
  onPressShowRepeatPassword,
}) => {
  const {colors} = useTheme();
  const styles = useThemedStyles(makeStyles);
  return (
    <SafeAreaView
      testID={tID('ChangeTemporaryPassword-SafeAreaView-Main')}
      style={styles.maincontainer}>
      <ScrollView
        testID={tID('ChangeTemporaryPassword-ScrollView-1')}
        style={styles.formView}>
        <LogoGroup width={146} height={40} style={CommonStyles.mv_40} />
        <View
          testID={tID('ChangeTemporaryPassword-View-SigninRouteContainer')}
          style={styles.signinRouteContainer}>
          <LeftArrowIcon width={5} height={9} tintColor={colors.textDisabled} />
          <Text
            testID={tID('ChangeTemporaryPassword-Text-SigninRoute')}
            variation="Text1SemiBold"
            color={colors.textDisabled}
            lineHeight={21}
            style={CommonStyles.mh_12}>
            Sign-In
          </Text>
          <LeftArrowIcon width={5} height={9} tintColor={colors.textDisabled} />
          <Text
            testID={tID('ChangeTemporaryPassword-Text-PersonalDetails')}
            variation="Text1SemiBold"
            color={colors.textDisabled}
            lineHeight={21}
            style={CommonStyles.mh_12}>
            Personal Details
          </Text>
          <LeftArrowIcon
            width={5}
            height={9}
            tintColor={colors.link}
          />
          <Text
            testID={tID('ChangeTemporaryPassword-Text-PhoneVerification')}
            variation="Text1SemiBold"
            color={colors.link}
            lineHeight={21}
            style={CommonStyles.ml_12}>
            Phone Verification
          </Text>
        </View>
        <Text
          testID={tID('ChangeTemporaryPassword-Text-Title')}
          variation="HeadlineBold28"
          color={colors.textPrimary}
          style={CommonStyles.mb_12}>
          Change Temporary Password
        </Text>
        <Text
          testID={tID('ChangeTemporaryPassword-Text-SubTitle')}
          variation="Body1Regular"
          color={colors.textSecondary}
          style={CommonStyles.mb_8}>
          You may want to change your password to something you will remember
          later. If you want to keep your password, select Skip Step.
        </Text>
        <Text
          testID={tID('ChangeTemporaryPassword-Text-SubTitle')}
          variation="Body1Regular"
          color={colors.textSecondary}
          style={CommonStyles.mb_8}>
          Password must be at least 8 characters and contain at least one
          capital letter, one special character, one number, and cannot be a
          common password.
        </Text>
        <View
          testID={tID('ChangeTemporaryPassword-View-BreadCrumbsContainer')}
          style={styles.dotContainer}>
          <BreadCrumbs
            current={1}
            total={1}
            activeColor={colors.link}
            currentPassiveColor={colors.link}
            passiveColor={colors.borderMuted}
            activeDotWidth={12}
            activeDotHeight={12}
            currentPassiveDotWidth={12}
            currentPassiveDotHeight={12}
            passiveDotWidth={12}
            passiveDotHeight={12}
            containerStyle={styles.marginLeftMinus4}
          />
          <BreadCrumbs
            current={1}
            total={1}
            activeColor={colors.link}
            currentPassiveColor={colors.link}
            passiveColor={colors.borderMuted}
            activeDotWidth={12}
            activeDotHeight={12}
            currentPassiveDotWidth={12}
            currentPassiveDotHeight={12}
            passiveDotWidth={12}
            passiveDotHeight={12}
            containerStyle={styles.marginLeftMinus10}
          />
          <BreadCrumbs
            current={1}
            total={2}
            activeColor={colors.link}
            currentPassiveColor={colors.link}
            passiveColor={colors.borderMuted}
            activeDotWidth={24}
            activeDotHeight={12}
            currentPassiveDotWidth={12}
            currentPassiveDotHeight={12}
            passiveDotWidth={12}
            passiveDotHeight={12}
            containerStyle={styles.doubleDot}
            rightDotStyle={styles.marginTopMinus3}
          />
        </View>
        <Divider />
        <View>
          <Input
            testID={tID('ChangeTemporaryPassword-Input-NewPassword')}
            hasError={hasPasswordError}
            errorText={'Password doesn’t meet the requirements.'}
            secureTextEntry={!showPassword}
            inputTitle={'New Password'}
            placeholder={'Type here...'}
            value={password}
            onChangeText={setPassword}
            topMargin={24}
            rightIcon={showPassword ? <OpenEyeIcon /> : <ClosedEyeIcon />}
            onPressRightIcon={onPressShowPassword}
            autoComplete={Platform.OS === 'web' ? 'new-password' : 'off'}
          />

          <Input
            testID={tID('ChangeTemporaryPassword-Input-RepeatPassword')}
            hasError={hasRepeatPasswordError}
            errorText={'Passwords do not match.'}
            secureTextEntry={!showRepeatPassword}
            inputTitle={'Repeat Password'}
            placeholder={'Type here...'}
            value={repeatPassword}
            onChangeText={setRepeatPassword}
            topMargin={24}
            rightIcon={showRepeatPassword ? <OpenEyeIcon /> : <ClosedEyeIcon />}
            onPressRightIcon={onPressShowRepeatPassword}
          />
        </View>
      </ScrollView>
      <View style={styles.buttonContainer}>
        <Button
          testID={tID('ChangeTemporaryPassword-Button-SkipStep')}
          title={'Skip Step'}
          titleColor={colors.link}
          style={styles.skipButton}
          onPress={onPressSkip}
        />
        <Button
          testID={tID('ChangeTemporaryPassword-Button-Save')}
          disabled={!isValidForm}
          title={'Save'}
          buttonType={BUTTON_TYPES.Filled}
          titleColor={colors.textOnAccent}
          style={styles.saveButton}
          onPress={onPressSave}
        />
      </View>
    </SafeAreaView>
  );
};

export default UI;
